import { SessionType } from "@app/shared/types";
import { Box, Button, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { GRAPHQL_QUERY_MY_SCHEDULE, GRAPHQL_QUERY_UPCOMING_EVENTS } from "app/queries";
import { routes } from "app/routes";
import { LIMITED_WIDTH_LARGE } from "app/styles";
import DocumentContext from "components/DocumentContext";
import PageWrapper from "components/PageWrapper";
import { LinkButton } from "features/navigation/LinkButton";
import { useCurrentServerTime } from "hooks/useCurrentServerTime";
import { EventListLoader } from "../eventDisplay/EventListLoader";
import { useHasMentorshipGroup } from "../mySanghas/useHasMentorshipGroup";
import {
    filterSessionsRightNow,
    sessionTypesForSessionsRightNow,
} from "./MemberSessionsRightNowPage";
import { MentorshipGroupPromoSection } from "./MentorshipGroupPromoSection";
import { BackLinkButton } from "components/BackButton";

const useStyles = makeStyles((theme) => ({
    buttonContainer: {
        textAlign: "center",
    },
    noMargin: {
        margin: 0,
    },
}));

export const MemberHomePage = () => {
    const classes = useStyles();

    const currentServerTime = useCurrentServerTime();
    const hasMentorshipGroup = useHasMentorshipGroup();

    const sectionTitle = (text: string) => <Typography variant="h3">{text}</Typography>;

    const seeMoreButton = (route: string) => (
        <Box className={classes.buttonContainer}>
            <LinkButton variant="secondary" to={route}>
                See more
            </LinkButton>
        </Box>
    );

    return (
        <div>
            <DocumentContext addBrandNameSuffix title="Home" />

            <PageWrapper data-testid="memberHomePage">
                <Typography variant="h1" className={classes.noMargin}>
                    Home
                </Typography>
                <EventListLoader
                    sx={{ mb: 10 }}
                    elementBefore={<Typography variant="h2">My Schedule</Typography>}
                    elementAfter={seeMoreButton(routes.memberSchedulePage())}
                    eventsQuery={GRAPHQL_QUERY_MY_SCHEDULE}
                    queryOptions={{ variables: { limit: 4 } }}
                    data-testid="mySchedule"
                    currentServerTime={currentServerTime}
                    limitDisplayedEvents={3}
                    showSpecificDate={true}
                />
                <Typography variant="h2">Explore</Typography>
                <EventListLoader
                    sx={{ mb: 10 }}
                    elementBefore={sectionTitle("Sessions Right Now")}
                    elementAfter={seeMoreButton(routes.memberSessionsRightNow())}
                    data-testid="sessionsRightNow"
                    currentServerTime={currentServerTime}
                    eventsQuery={GRAPHQL_QUERY_UPCOMING_EVENTS}
                    limitDisplayedEvents={3}
                    queryOptions={{
                        variables: {
                            includeTypes: sessionTypesForSessionsRightNow,
                            startsWithinMinutes: 15,
                            limit: 8,
                        },
                    }}
                    filterEvents={filterSessionsRightNow}
                />
                <EventListLoader
                    sx={{ mb: 10 }}
                    elementBefore={sectionTitle("Upcoming Mindful Friends Groups")}
                    elementAfter={seeMoreButton(routes.memberMindfulFriends())}
                    data-testid="upcomingInterestGroups"
                    currentServerTime={currentServerTime}
                    eventsQuery={GRAPHQL_QUERY_UPCOMING_EVENTS}
                    queryOptions={{
                        variables: {
                            includeTypes: [SessionType.INTEREST_GROUP],
                            startsAfterMinutes: 15,
                            limit: 2,
                        },
                    }}
                />
                <EventListLoader
                    sx={{ mb: 10 }}
                    elementBefore={sectionTitle("Upcoming Talks")}
                    data-testid="upcomingTalks"
                    currentServerTime={currentServerTime}
                    eventsQuery={GRAPHQL_QUERY_UPCOMING_EVENTS}
                    queryOptions={{
                        variables: {
                            includeTypes: [SessionType.COMMUNITY_TALK],
                            startsAfterMinutes: 15,
                        },
                    }}
                />
                <EventListLoader
                    sx={{ mb: 10 }}
                    elementBefore={sectionTitle("Upcoming Meditations")}
                    elementAfter={seeMoreButton(
                        routes.memberDropInSessionsMeditationsDiscussions(),
                    )}
                    data-testid="upcomingDropInSessions"
                    currentServerTime={currentServerTime}
                    eventsQuery={GRAPHQL_QUERY_UPCOMING_EVENTS}
                    queryOptions={{
                        variables: {
                            includeTypes: [
                                SessionType.COMMUNITY_SIT,
                                SessionType.COMMUNITY_DISCUSSION_GROUP,
                                SessionType.SILENT_SIT,
                                SessionType.TEACHER_ONLY_SESSION, // These are filtered out on the back-end for members
                                SessionType.OPEN_HOUSE,
                            ],
                            startsAfterMinutes: 15,
                            limit: 2,
                        },
                    }}
                />
                {!hasMentorshipGroup && (
                    <MentorshipGroupPromoSection
                        elementBefore={sectionTitle("Mentorship Groups")}
                        elementAfter={seeMoreButton(routes.memberSanghas())}
                        data-testid="mentorshipGroupPromoSection"
                    />
                )}
            </PageWrapper>
        </div>
    );
};
